/**
 * Utilitários para trabalhar com URLs de embed de vídeo (YouTube e Vimeo).
 *
 * A API de vídeos promocionais já retorna o campo `embed_url` normalizado
 * (`youtube.com/embed/{id}` ou `player.vimeo.com/video/{id}`), preservando
 * o hash de privacidade do Vimeo quando existir (`?h={hash}`).
 * Estas funções apenas complementam essa URL com os parâmetros de player
 * necessários para o popup (autoplay mudo, playsinline etc.).
 */

export type VideoProvider = 'youtube' | 'vimeo' | 'unknown';

interface BuildAutoplayOptions {
  /** Reproduzir automaticamente. Padrão: true. */
  autoplay?: boolean;
  /** Iniciar mutado (obrigatório para autoplay funcionar na maioria dos browsers). Padrão: true. */
  muted?: boolean;
}

/**
 * Valida se a URL usa um esquema HTTP(S) seguro, evitando esquemas perigosos
 * como `javascript:` ou `data:` que poderiam ser injetados via `href`/`src`.
 * Usa `new URL()` para parse robusto (mesma abordagem de `getVideoProvider`).
 */
export const isSafeHttpUrl = (url?: string | null): boolean => {
  if (!url) return false;

  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Identifica o provedor do vídeo a partir do host da URL (não por substring,
 * evitando spoofing como `evil.com/?u=youtube.com`).
 */
export const getVideoProvider = (url?: string | null): VideoProvider => {
  if (!url) return 'unknown';

  try {
    const { hostname } = new URL(url);

    if (hostname === 'youtu.be' || /(^|\.)youtube\.com$/.test(hostname)) {
      return 'youtube';
    }

    if (/(^|\.)vimeo\.com$/.test(hostname)) {
      return 'vimeo';
    }

    return 'unknown';
  } catch {
    return 'unknown';
  }
};

/**
 * Extrai o id de um vídeo do YouTube a partir de uma URL de embed/watch/short.
 */
export const getYoutubeId = (url?: string | null): string | null => {
  if (getVideoProvider(url) !== 'youtube' || !url) return null;

  try {
    const parsed = new URL(url);

    if (parsed.hostname === 'youtu.be') {
      return parsed.pathname.replace('/', '') || null;
    }

    const watchId = parsed.searchParams.get('v');
    if (watchId) return watchId;

    const match = parsed.pathname.match(/\/(embed|shorts)\/([^/?]+)/);
    return match ? match[2] : null;
  } catch {
    return null;
  }
};

/**
 * Retorna a thumbnail padrão de um vídeo do YouTube (para exibição enquanto
 * o iframe carrega). Vimeo exige chamada de API, então retornamos null e o
 * componente usa um placeholder visual.
 */
export const getVideoThumbnail = (url?: string | null): string | null => {
  const youtubeId = getYoutubeId(url);
  if (youtubeId) {
    return `https://img.youtube.com/vi/${youtubeId}/hqdefault.jpg`;
  }
  return null;
};

/**
 * Complementa a `embed_url` com os parâmetros de autoplay/mute apropriados
 * para cada provedor, preservando qualquer query string já existente
 * (ex.: hash de privacidade do Vimeo).
 */
export const buildAutoplayEmbedUrl = (
  embedUrl?: string | null,
  { autoplay = true, muted = true }: BuildAutoplayOptions = {}
): string => {
  if (!embedUrl) return '';

  const provider = getVideoProvider(embedUrl);
  const params: string[] = [];

  if (autoplay) params.push('autoplay=1');

  if (provider === 'youtube') {
    if (muted) params.push('mute=1');
    params.push('playsinline=1');
    params.push('rel=0');
    params.push('modestbranding=1');
  } else if (provider === 'vimeo') {
    if (muted) params.push('muted=1');
    params.push('playsinline=1');
  } else {
    // Provedor desconhecido: não montamos URL de embed para host não verificado.
    return '';
  }

  if (params.length === 0) return embedUrl;

  const separator = embedUrl.includes('?') ? '&' : '?';
  return `${embedUrl}${separator}${params.join('&')}`;
};
